import { LOCALES, LOCALE_NAMES, LOCALE_TAGS, isLocale, type Locale } from '@app/shared'

import type { Dictionary } from './ru'

/**
 * Widens the literal types the source dictionary gets from `as const`, so a
 * translation must match the *shape* exactly — every key, every function
 * signature — without having to repeat the Russian text.
 *
 * The practical effect: adding a string to `ru.ts` and forgetting one of the
 * other dictionaries is a type error, and so is renaming a key on one side only.
 */
type Loosen<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => R
    : { [K in keyof T]: Loosen<T[K]> }

export type Translations = Loosen<Dictionary>

/**
 * The interface language a profile asks for. The server stores what the app
 * sent, but also accepts a full BCP-47 tag (`en-GB`, `pt-BR`), so only the
 * language part decides; anything the app does not ship reads as no choice.
 */
export function profileLocaleOf(language: unknown): Locale | null {
  if (typeof language !== 'string') return null
  const short = language.trim().toLowerCase().split('-')[0] ?? ''
  return isLocale(short) ? short : null
}

export { LOCALES, LOCALE_NAMES, LOCALE_TAGS, isLocale }
export type { Locale }
